// Yandex Metrica Service
// Управление загрузкой и инициализацией Яндекс.Метрики
import { logger } from '../utils/logger';

declare global {
  interface Window {
    ym?: (counterId: number, method: string, ...args: any[]) => void;
    dataLayer?: any[];
  }
}

const YANDEX_METRICA_ID = parseInt(import.meta.env.VITE_YANDEX_METRICA_ID || '110154031', 10);

interface MetricaConfig {
  ssr?: boolean;
  webvisor?: boolean;
  clickmap?: boolean;
  ecommerce?: string;
  accurateTrackBounce?: boolean;
  trackLinks?: boolean;
}

class YandexMetricaService {
  private isInitialized = false;
  private isConsentGiven = false;
  private pendingEvents: Array<{ method: string; args: any[] }> = [];
  private onReadyCallbacks: Array<() => void> = [];

  /**
   * Проверка, дано ли согласие на cookies
   */
  checkConsent(): boolean {
    const consent = localStorage.getItem('cookie_consent');
    return consent === 'true';
  }

  /**
   * Получить дату согласия
   */
  getConsentDate(): string | null {
    return localStorage.getItem('cookie_consent_date');
  }

  /**
   * Отозвать согласие
   */
  revokeConsent(): void {
    localStorage.setItem('cookie_consent', 'false');
    this.isConsentGiven = false;
    logger.info('Cookie consent revoked');
  }

  /**
   * Дать согласие и инициализировать Метрику
   */
  giveConsent(): boolean {
    localStorage.setItem('cookie_consent', 'true');
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    this.isConsentGiven = true;

    // Инициализируем Метрику
    this.init();

    return true;
  }

  /**
   * Register callback to be called when Metrica is fully initialized
   */
  onReady(callback: () => void): void {
    if (this.isInitialized) {
      callback();
    } else {
      this.onReadyCallbacks.push(callback);
    }
  }

  /**
   * Инициализация Яндекс.Метрики
   * Загружает скрипт только после согласия
   */
  init(config: MetricaConfig = {}): void {
    if (this.isInitialized || !this.isConsentGiven) {
      return;
    }

    if (YANDEX_METRICA_ID === 0) {
      console.warn('Yandex Metrica ID not configured');
      return;
    }

    logger.info(`Initializing Yandex Metrica (ID: ${YANDEX_METRICA_ID})`);
    console.log('[Metrica] Initializing Yandex Metrica, ID:', YANDEX_METRICA_ID);
    this.isInitialized = true;

    // Создаём dataLayer для ecommerce
    window.dataLayer = window.dataLayer || [];

    const m = window as any;
    const counterId = YANDEX_METRICA_ID;

    // Создаём stub если его нет
    if (!m.ym) {
      m.ym = function() { (m.ym.a = m.ym.a || []).push(arguments); };
      m.ym.l = Date.now();
      console.log('[Metrica] stub created');
    }

    // Загружаем tag.js
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://mc.yandex.ru/metrika/tag.js';

    script.onload = () => {
      console.log('[Metrica] tag.js loaded, calling ym init');
      // Теперь вызываем init — tag.js должен заменить ym и обработать очередь
      m.ym(counterId, 'init', {
        ssr: config.ssr ?? true,
        webvisor: config.webvisor ?? true,
        clickmap: config.clickmap ?? true,
        ecommerce: config.ecommerce ?? 'dataLayer',
        referrer: document.referrer,
        accurateTrackBounce: config.accurateTrackBounce ?? true,
        trackLinks: config.trackLinks ?? true,
      });

      // Проверяем обработалась ли очередь
      setTimeout(() => {
        const queueLen = m.ym?.a?.length ?? -1;
        console.log('[Metrica] 500ms after init: queue len:', queueLen);
        if (queueLen > 0) {
          console.log('[Metrica] queue:', JSON.stringify(m.ym.a));
          // FALLBACK: tag.js не заменил stub — отправляем hit напрямую через HTTP
          console.log('[Metrica] sending hit directly via HTTP fallback');
          const params = new URLSearchParams({
            'browser-info': `vh:1920x1080:pf:1417128469414:rn:123456789:fu:1`,
            'cl': '24bpp',
            'fl': '0',
            'rn': Math.random().toString(),
            'hit-url': window.location.href,
            'title': document.title,
            'page-url': window.location.href,
            'browser-lang': navigator.language,
          });
          const img = new Image();
          img.src = `https://mc.yandex.ru/watch/${counterId}?${params.toString()}`;
          img.onload = () => console.log('[Metrica] HTTP hit sent successfully');
          img.onerror = () => console.error('[Metrica] HTTP hit failed');
        } else {
          console.log('[Metrica] SUCCESS: queue processed');
        }
      }, 500);
    };

    script.onerror = () => {
      console.error('[Metrica] tag.js FAILED to load');
    };

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    console.log('[Metrica] tag.js script inserted, calling ym reachGoal');

    // Вызываем reachGoal до init (попадёт в очередь)
    m.ym(counterId, 'reachGoal', 'cookie_accepted');

    // Setup tracking
    this.setupClickTracking();
    this.setupTimeOnPage();

    // Notify callbacks
    this.onReadyCallbacks.forEach(cb => cb());
    this.onReadyCallbacks = [];
  }

  /**
   * Отправка события в Метрику
   */
  hit(target?: string, params?: Record<string, any>): void {
    if (!this.isConsentGiven) {
      return;
    }

    // Используем HTTP fallback — tag.js не обрабатывает queue
    this._sendViaHTTP('hit', target, params);
  }

  /**
   * Отправка цели (goal)
   */
  reachGoal(goalName: string, params?: Record<string, any>): void {
    if (!this.isConsentGiven) {
      return;
    }

    // Используем HTTP fallback
    this._sendViaHTTP('reachGoal', goalName, params);
    logger.info(`Metrica goal reached: ${goalName}`);
  }

  /**
   * Отправка параметров пользователя
   */
  userParams(params: Record<string, any>): void {
    if (!this.isConsentGiven) {
      return;
    }

    // Используем ym если доступна, иначе HTTP
    const m = window as any;
    if (m.ym && !m.ym.a) {
      m.ym(YANDEX_METRICA_ID, 'userParams', params);
    }
  }

  /**
   * HTTP fallback для отправки событий в Метрику
   */
  private _sendViaHTTP(method: string, target?: string, params?: Record<string, any>): void {
    const m = window as any;
    // Если ym реальная (не stub) — используем её
    if (m.ym && !m.ym.a) {
      if (method === 'hit') {
        m.ym(YANDEX_METRICA_ID, 'hit', target, params);
      } else if (method === 'reachGoal') {
        m.ym(YANDEX_METRICA_ID, 'reachGoal', target, params);
      }
      return;
    }

    // Fallback: pixel tracking
    const searchParams = new URLSearchParams({
      'rn': Math.random().toString(),
      'page-url': window.location.href,
      'title': document.title,
      'browser-lang': navigator.language,
    });

    if (method === 'reachGoal' && target) {
      searchParams.set('goal', target);
    } else if (method === 'hit' && target) {
      searchParams.set('hit-url', target);
    }

    if (params) {
      searchParams.set('params', JSON.stringify(params));
    }

    const img = new Image();
    img.src = `https://mc.yandex.ru/watch/${YANDEX_METRICA_ID}?${searchParams.toString()}`;
    img.onload = () => logger.debug(`Metrica ${method} sent via HTTP`);
  }

  /**
   * Получить статус инициализации
   */
  isReady(): boolean {
    return this.isInitialized && this.isConsentGiven;
  }

  /**
   * Настроить отправку событий кликов
   */
  setupClickTracking(): void {
    if (!this.isReady()) {
      return;
    }

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      // Отслеживаем клики по ссылкам
      if (target.tagName === 'A') {
        this.hit('link_click', {
          url: target.href,
          text: target.textContent?.trim(),
        });
      }

      // Отслеживаем клики по кнопкам
      if (target.tagName === 'BUTTON' || target.closest('button')) {
        const button = target.tagName === 'BUTTON' ? target : target.closest('button');
        this.hit('button_click', {
          text: button?.textContent?.trim(),
          class: button?.className,
        });
      }
    });
  }

  /**
   * Отслеживание времени на странице
   */
  setupTimeOnPage(): void {
    if (!this.isReady()) {
      return;
    }

    const startTime = Date.now();

    window.addEventListener('beforeunload', () => {
      const timeOnPage = Math.round((Date.now() - startTime) / 1000);
      this.hit('time_on_page', { seconds: timeOnPage });
    });
  }
}

// Экспортируем singleton
export const yandexMetrica = new YandexMetricaService();
export default yandexMetrica;
